/*
  Akash Vinchankar
  08 Sept 2023

 * BulkActionButtons Component
 
 * This component renders a set of action buttons for bulk actions.
 * It includes a selection, a close button, and action buttons.
 * The action buttons can be customized with various properties like text, icon, onClick handler.
 * The `disabled` prop can be used to enable or disable individual buttons.
 *
 * Props:
 * - buttons: An array of button configurations, text, onClick handler and other properties.
 * - selection: An array of selection information.
 * - isOpen: A boolean indicating whether the component is open or not.
 * - onClose: A function to handle the close action.
 
 */

import React from "react";
import { BaseButton } from "prefab";
import classNames from "classnames";
import { BUTTON_TYPE, SIZE, ICON_POS } from "prefab";
import LocalizedButton from "../common/LocalizedButton";
import BulkActionButtonsStyles from "../../styles/BulkActionButtons.module.scss";

export default function BulkActionButtons({
  buttons = [],
  selection = [],
  isOpen = false,
  onClose = () => {},
}) {
  if (!isOpen) return null;
  return (
    <div className={BulkActionButtonsStyles.actionButtonsContainer}>
      <div className={BulkActionButtonsStyles.selectionInfoContainer}>
        <div className="flex align-center justify-start">
          {selection.map(({ count, label }, index) => (
            <div className={BulkActionButtonsStyles.selectionInfo} key={index}>
              <span>{count}</span> {label} {index + 1 === selection.length ? "Selected" : null}
            </div>
          ))}
        </div>
        <BaseButton
          iconName="CancelIcon"
          buttonType={BUTTON_TYPE.SECONDARY}
          buttonSize={SIZE.SMALL}
          onClick={onClose}
        />
      </div>
      <div
        className={classNames(
          "flex align-center justify-start",
          BulkActionButtonsStyles.buttonList
        )}
      >
        {buttons.map(
          (
            {
              text = "",
              onClick,
              icon,
              className = "",
              checkScope = false,
              scope,
              scopeAction,
              userData,
              isIconRight = false,
              isHidden,
            },
            index
          ) => {
            const withScopeProps = checkScope
              ? {
                  scope,
                  scopeAction,
                  userData,
                }
              : null;
            /* SLATE-1341 08 Sept 2023 Akash Vinchankar :- Check if is passed as a prop or not else default value is false */
            const disabledProp = "disabled" in buttons[index] ? buttons[index].disabled : false;

            return (
              !isHidden && (
                <LocalizedButton
                  className={className}
                  buttonType={BUTTON_TYPE.SECONDARY}
                  iconName={icon}
                  iconPos={isIconRight ? ICON_POS.RIGHT : ICON_POS.LEFT}
                  onClick={onClick}
                  text={text}
                  key={index}
                  /* SLATE-1341 08 Sept 2023 Akash Vinchankar :- Disabled optional prop added to this button */
                  // disabled={disabled}
                  disabled={disabledProp}
                  {...withScopeProps}
                />
              )
            );
          }
        )}
      </div>
    </div>
  );
}
